import {useConfigurateurStore} from "../stores/configurateur";
import {useGlobalLoaderStore} from "../stores/globalLoader";
import {h} from 'vue';
import {useConfirmModal} from "../composables/useConfirmModal";
import {Modal} from "bootstrap";
import Logger from "./logger";

const logger = new Logger('directive')

/**
 * Classe d'assistance pour les fonctionnalités du configurateur
 * Extrait les méthodes du bloc mounted de openConfigurateurDirective pour une réutilisation
 */
export default class ConfigurateurHelpers {
    constructor(params = {}) {
        // Paramètres principaux
        this.el = params.el;
        this.binding = params.binding;
        this.store = params.store || useConfigurateurStore();
        this.storeGlobalLoader = params.storeGlobalLoader || useGlobalLoaderStore();

        // Variables de contexte
        this.stage = params.stage || false;
        this.circuit = params.circuit || false;
        this.voiture = params.voiture || false;
        this.id = params.id || false;
        this.isBapteme = params.isBapteme;
        this.voitureId = params.voitureId || null;
        this.circuitId = params.circuitId || null;
    }

    /**
     * Récupération des options actuelles passées à la directive
     */
    getOptions() {
        // Gérer le cas où this.el peut être null/undefined
        if (!this.el || !this.el.__openConfigurateurOptions) {
            return {};
        }
        return (typeof this.el.__openConfigurateurOptions === 'object')
            ? this.el.__openConfigurateurOptions
            : {};
    }

    /**
     * Sélection explicitement demandée par le bouton courant.
     *
     * Les raccourcis historiques utilisent des modificateurs Vue alors que le
     * configurateur rapide transmet un objet générique. Les deux formes doivent
     * être comparées au store avant de décider de reprendre sa configuration.
     */
    getRequestedSelection() {
        const options = this.getOptions();
        const bindingValue = this.binding?.value;
        const modifierId = bindingValue && typeof bindingValue === 'object'
            ? bindingValue.id
            : bindingValue;

        return {
            stageId: options.stage_id ?? (this.stage ? modifierId : null),
            voitureId: options.voiture_id
                ?? options.voiture?.id
                ?? (this.voiture ? this.voitureId : null),
            circuitId: options.circuit_id
                ?? options.circuit?.id
                ?? (this.circuit ? this.circuitId : null),
            nbTours: options.nb_tours ?? null,
        };
    }

    /**
     * Indique si le store satisfait déjà toutes les valeurs explicites du bouton.
     */
    isSameConfiguration() {
        const requested = this.getRequestedSelection();
        const comparisons = [
            [requested.stageId, this.store.stage_id],
            [requested.voitureId, this.store.voiture_id],
            [requested.circuitId, this.store.circuit_id],
            [requested.nbTours, this.store.nb_tours],
        ];
        const explicitComparisons = comparisons.filter(([value]) => (
            value !== null && value !== undefined && value !== ''
        ));

        // Les boutons sans présélection conservent le comportement historique.
        return explicitComparisons.length === 0
            || explicitComparisons.every(([requestedValue, storedValue]) => (
                String(requestedValue) === String(storedValue)
            ));
    }

    /**
     * Fonction pour gérer le choix du type d'expérience
     */
    handleExperienceChoice() {
        // Toujours lire la valeur courante passée à la directive
        const options = this.getOptions();

        // Résoudre l'expérience depuis les modifiers ou les options
        const resolvedExperience = (() => {
            if (this.isBapteme !== undefined) {
                return this.isBapteme ? 'bapteme' : 'pilotage';
            }
            const rawExp = options?.experience;
            const exp = (typeof rawExp === 'string') ? rawExp.toLowerCase().trim() : rawExp;
            if (exp === 'pilotage' || exp === 'bapteme') {
                return exp;
            }
            if (typeof options?.is_bapteme !== 'undefined') {
                return options.is_bapteme ? 'bapteme' : 'pilotage';
            }
            return null;
        })();

        if (resolvedExperience) {
            logger.log('🎯 Directive - experience résolue:', resolvedExperience);
            this.store.setExperience(resolvedExperience);
            this.proceedWithConfigurateur();
        } else {
            logger.log('🎯 Directive - experience non définie, affichage popup');
            this.showExperienceChoicePopup();
        }
    }

    /**
     * Fonction pour afficher le popup de reprise de configuration
     */
    showResumeConfigPopup() {
        // Utiliser le composable pour gérer le modal de confirmation
        let messageModal = [
            'Vous avez déjà commencé une configuration avec la voiture ',
            h('strong', {}, this.store.voiture.marque + ' ' + this.store.voiture.designation),
            ' sur le circuit ',
            h('strong', {}, this.store.circuit.designation),
            '. Voulez-vous recommencer une nouvelle configuration?'
        ];

        if (!this.store.voiture.designation) {
            messageModal = [
                'Vous avez déjà commencé une configuration avec une voiture',
                '. Voulez-vous recommencer une nouvelle configuration?'
            ]
        }
        const {showModal} = useConfirmModal({
            title: 'Confirmation',
            confirmButtonText: 'Nouvelle configuration',
            cancelButtonText: 'Annuler',
            anotherButtonText: 'Continuer avec la configuration existante',
            confirmButtonClass: 'btn-warning',
            anotherButtonClass: 'btn-primary',
            modalDialogClass: 'modal-lg',
            onConfirm: async () => {
                // Nouvelle configuration - gérer l'expérience puis processConfigurateur directement
                this.handleExperienceChoiceForNewConfig();
            },
            onAnother: async () => {
                // Continuer configuration existante - pas besoin de choix d'expérience
                try {
                    // Gérer le cas où this.el peut être null/undefined
                    if (this.el) {
                        this.el.classList.add('is-opening-configurateur');
                        this.el.style.cursor = 'wait';
                    }
                    this.store.setStep(this.store.first_step);
                    this.storeGlobalLoader.start()
                    await this.store.openModal();
                    this.storeGlobalLoader.stop()
                } catch (error) {
                    console.error("Erreur lors de l'ouverture du configurateur via directive:", error);
                } finally {
                    // Gérer le cas où this.el peut être null/undefined
                    if (this.el) {
                        this.el.classList.remove('is-opening-configurateur');
                        this.el.style.cursor = '';
                    }
                }
            },
            onCancel: () => {
                // Rien à faire, fermer simplement
            },
            renderContent: () => h('p', {}, messageModal)
        });

        // Afficher le modal
        showModal();
    }

    /**
     * Fonction spéciale pour gérer le choix d'expérience dans le contexte d'une nouvelle configuration
     */
    handleExperienceChoiceForNewConfig() {
        // Toujours lire la valeur courante passée à la directive
        const options = this.getOptions();

        const resolvedExperience = (() => {
            if (this.isBapteme !== undefined) {
                return this.isBapteme ? 'bapteme' : 'pilotage';
            }
            const rawExp = options?.experience;
            const exp = (typeof rawExp === 'string') ? rawExp.toLowerCase().trim() : rawExp;
            if (exp === 'pilotage' || exp === 'bapteme') {
                return exp;
            }
            if (typeof options?.is_bapteme !== 'undefined') {
                return options.is_bapteme ? 'bapteme' : 'pilotage';
            }
            return null;
        })();

        if (resolvedExperience) {
            logger.log('🎯 Directive - experience résolue pour nouvelle config:', resolvedExperience);
            this.store.setExperience(resolvedExperience);
            this.directProcessConfigurateur();
        } else {
            logger.log('🎯 Directive - experience non définie pour nouvelle config, affichage popup');
            this.showExperienceChoicePopupForNewConfig();
        }
    }

    /**
     * Fonction spéciale pour afficher le popup de choix d'expérience pour nouvelle config
     */
    showExperienceChoicePopupForNewConfig() {
        // Créer le popup HTML dynamiquement
        const popupHtml = `
            <div id="experience-choice-popup-new" class="modal fade" tabindex="-1" aria-labelledby="experience-choice-label" aria-hidden="true">
                <div class="modal-dialog modal-dialog-centered">
                    <div class="modal-content">
                        <div class="modal-header">
                            <h5 class="modal-title" id="experience-choice-label">Choisissez votre expérience</h5>
                            <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
                        </div>
                        <div class="modal-body">
                            <p class="text-center mb-4">Quel type d'expérience souhaitez-vous vivre ?</p>
                            <div class="row g-3">
                                <div class="col-6">
                                    <div class="card h-100 text-center experience-choice-card" style="cursor: pointer;" data-experience="pilotage">
                                        <div class="card-body d-flex flex-column justify-content-center align-items-center p-4">
                                            <i class="bi bi-person-check-fill fs-1 text-warning mb-3"></i>
                                            <h5 class="card-title fw-bold">Pilotage</h5>
                                            <p class="card-text small text-muted">Vous conduisez la voiture</p>
                                        </div>
                                    </div>
                                </div>
                                <div class="col-6">
                                    <div class="card h-100 text-center experience-choice-card" style="cursor: pointer;" data-experience="bapteme">
                                        <div class="card-body d-flex flex-column justify-content-center align-items-center p-4">
                                            <i class="bi bi-person-arms-up fs-1 text-info mb-3"></i>
                                            <h5 class="card-title fw-bold">Baptême Passager</h5>
                                            <p class="card-text small text-muted">Un pilote conduit pour vous</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <style>
                .experience-choice-card {
                    border: 2px solid #e9ecef;
                    transition: all 0.2s ease-in-out;
                }
                .experience-choice-card:hover {
                    border-color: #007bff;
                    box-shadow: 0 4px 12px rgba(0, 123, 255, 0.15);
                    transform: translateY(-2px);
                }
            </style>
        `;

        // Supprimer le popup existant s'il y en a un
        const existingPopup = document.getElementById('experience-choice-popup-new');
        if (existingPopup) {
            existingPopup.remove();
        }

        // Ajouter le popup au body
        document.body.insertAdjacentHTML('beforeend', popupHtml);

        // Initialiser le modal Bootstrap
        const modal = new Modal('#experience-choice-popup-new');

        // Ajouter les event listeners pour les choix
        document.querySelectorAll('#experience-choice-popup-new [data-experience]').forEach(card => {
            card.addEventListener('click', () => {
                const experienceType = card.getAttribute('data-experience');
                logger.log('🎯 Popup nouvelle config - Choix d\'expérience cliqué:', experienceType);
                this.store.setExperience(experienceType);
                logger.log('🎯 Popup nouvelle config - Experience set dans le store, valeur actuelle:', this.store.experience);
                modal.hide();

                // Procéder avec processConfigurateur directement après un délai pour permettre à la modal de se fermer
                setTimeout(() => {
                    logger.log('🎯 Popup nouvelle config - Procédure avec processConfigurateur après timeout');
                    this.directProcessConfigurateur();
                }, 300);
            });
        });

        // Nettoyer le popup après fermeture
        document.getElementById('experience-choice-popup-new').addEventListener('hidden.bs.modal', () => {
            document.getElementById('experience-choice-popup-new').remove();
        });

        // Afficher le popup
        modal.show();
    }

    /**
     * Fonction pour appeler directement processConfigurateur avec gestion d'erreur
     */
    async directProcessConfigurateur() {
        try {
            // Gérer le cas où this.el peut être null/undefined
            if (this.el) {
                this.el.classList.add('is-opening-configurateur');
                this.el.style.cursor = 'wait';
            }
            await this.processConfigurateur();
        } catch (error) {
            console.error("Erreur lors de l'ouverture du configurateur via directive:", error);
        } finally {
            // Gérer le cas où this.el peut être null/undefined
            if (this.el) {
                this.el.classList.remove('is-opening-configurateur');
                this.el.style.cursor = '';
            }
        }
    }

    /**
     * Fonction pour afficher le popup de choix d'expérience
     */
    showExperienceChoicePopup() {
        // Créer le popup HTML dynamiquement
        const popupHtml = `
            <div id="experience-choice-popup" class="modal fade" tabindex="-1" aria-labelledby="experience-choice-label" aria-hidden="true">
                <div class="modal-dialog modal-dialog-centered">
                    <div class="modal-content">
                        <div class="modal-header">
                            <h5 class="modal-title" id="experience-choice-label">Choisissez votre expérience</h5>
                            <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
                        </div>
                        <div class="modal-body">
                            <p class="text-center mb-4">Quel type d'expérience souhaitez-vous vivre ?</p>
                            <div class="row g-3">
                                <div class="col-6">
                                    <div class="card h-100 text-center experience-choice-card" style="cursor: pointer;" data-experience="pilotage">
                                        <div class="card-body d-flex flex-column justify-content-center align-items-center p-4">
                                            <i class="bi bi-person-check-fill fs-1 text-warning mb-3"></i>
                                            <h5 class="card-title fw-bold">Pilotage</h5>
                                            <p class="card-text small text-muted">Vous conduisez la voiture</p>
                                        </div>
                                    </div>
                                </div>
                                <div class="col-6">
                                    <div class="card h-100 text-center experience-choice-card" style="cursor: pointer;" data-experience="bapteme">
                                        <div class="card-body d-flex flex-column justify-content-center align-items-center p-4">
                                            <i class="bi bi-person-arms-up fs-1 text-info mb-3"></i>
                                            <h5 class="card-title fw-bold">Baptême Passager</h5>
                                            <p class="card-text small text-muted">Un pilote conduit pour vous</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <style>
                .experience-choice-card {
                    border: 2px solid #e9ecef;
                    transition: all 0.2s ease-in-out;
                }
                .experience-choice-card:hover {
                    border-color: #007bff;
                    box-shadow: 0 4px 12px rgba(0, 123, 255, 0.15);
                    transform: translateY(-2px);
                }
            </style>
        `;

        // Supprimer le popup existant s'il y en a un
        const existingPopup = document.getElementById('experience-choice-popup');
        if (existingPopup) {
            existingPopup.remove();
        }

        // Ajouter le popup au body
        document.body.insertAdjacentHTML('beforeend', popupHtml);

        // Initialiser le modal Bootstrap
        const modal = new Modal('#experience-choice-popup');

        // Ajouter les event listeners pour les choix
        document.querySelectorAll('[data-experience]').forEach(card => {
            card.addEventListener('click', () => {
                const experienceType = card.getAttribute('data-experience');
                logger.log('🎯 Popup - Choix d\'expérience cliqué:', experienceType);
                this.store.setExperience(experienceType);
                logger.log('🎯 Popup - Experience set dans le store, valeur actuelle:', this.store.experience);
                modal.hide();

                // Procéder avec l'ouverture du configurateur après un délai pour permettre à la modal de se fermer
                setTimeout(() => {
                    logger.log('🎯 Popup - Procédure avec configurateur après timeout');
                    this.proceedWithConfigurateur();
                }, 300);
            });
        });

        // Nettoyer le popup après fermeture
        document.getElementById('experience-choice-popup').addEventListener('hidden.bs.modal', () => {
            document.getElementById('experience-choice-popup').remove();
        });

        // Afficher le popup
        modal.show();
    }

    /**
     * Fonction pour procéder avec l'ouverture du configurateur
     */
    async proceedWithConfigurateur() {
        // Ajouter la classe et le curseur d'attente - Gérer le cas où this.el peut être null/undefined
        if (this.el) {
            this.el.classList.add('is-opening-configurateur');
            this.el.style.cursor = 'wait';
        }

        if (this.isSameConfiguration()) {
            this.store.setStep(this.store.first_step);
            this.storeGlobalLoader.start()
            await this.store.openModal({circuitSelectedByUser: Boolean(this.getRequestedSelection().circuitId)});
            this.storeGlobalLoader.stop()
            return;
        }

        // Vérifier si un configurateur est déjà en cours avec une voiture et un circuit
        if (this.store.voiture_id && this.store.circuit_id && this.el) {
            // Utiliser le composable pour gérer le modal de confirmation

            let messageModal = [
                'Vous avez déjà commencé une configuration avec la voiture ',
                h('strong', {}, this.store.voiture.marque + ' ' + this.store.voiture.designation),
                ' sur le circuit ',
                h('strong', {}, this.store.circuit.designation),
                '. Voulez-vous recommencer une nouvelle configuration?'
            ];

            if (!this.store.voiture.designation) {
                messageModal = [
                    'Vous avez déjà commencé une configuration avec une voiture',
                    '. Voulez-vous recommencer une nouvelle configuration?'
                ]
            }

            const {showModal} = useConfirmModal({
                title: 'Confirmation',
                confirmButtonText: 'Nouvelle configuration',
                cancelButtonText: 'Annuler',
                anotherButtonText: 'Continuer avec la configuration existante',
                confirmButtonClass: 'btn-warning',
                anotherButtonClass: 'btn-primary',
                modalDialogClass: 'modal-lg',
                onConfirm: async () => {
                    try {
                        // Réinitialiser le store et continuer avec la logique normale
                        await this.processConfigurateur();
                    } catch (error) {
                        console.error("Erreur lors de l'ouverture du configurateur via directive:", error);
                    } finally {
                        // Retirer la classe et le curseur d'attente dans tous les cas
                        if (this.el) {
                            this.el.classList.remove('is-opening-configurateur');
                            this.el.style.cursor = '';
                        }
                    }
                },
                onAnother: async () => {
                    try {
                        // Réinitialiser le store et continuer avec la logique normale
                        this.store.setStep(this.store.first_step);
                        this.storeGlobalLoader.start()
                        await this.store.openModal();
                        this.storeGlobalLoader.stop()
                    } catch (error) {
                        console.error("Erreur lors de l'ouverture du configurateur via directive:", error);
                    } finally {
                        // Retirer la classe et le curseur d'attente dans tous les cas
                        if (this.el) {
                            this.el.classList.remove('is-opening-configurateur');
                            this.el.style.cursor = '';
                        }
                    }
                },
                onCancel: () => {
                    // Réinitialiser l'état du bouton
                    if (this.el) {
                        this.el.classList.remove('is-opening-configurateur');
                        this.el.style.cursor = '';
                    }
                },
                renderContent: () => h('p', {}, messageModal)
            });

            // Afficher le modal
            showModal();
            return; // Sortir de la fonction pour éviter d'exécuter le reste du code
        }

        try {
            // Log avant process pour diagnostiquer la propagation
            logger.log('🎯 Directive - before process, store.preselectedDateIso:', this.store.preselectedDateIso);
            await this.processConfigurateur();
        } catch (error) {
            console.error("Erreur lors de l'ouverture du configurateur via directive:", error);
            // Optionnel: Afficher une notification d'erreur à l'utilisateur
        } finally {
            // Retirer la classe et le curseur d'attente dans tous les cas
            if (this.el) {
                this.el.classList.remove('is-opening-configurateur');
                this.el.style.cursor = '';
            }
        }
    }

    /**
     * Fonction pour traiter la logique du configurateur
     */
    async processConfigurateur() {
        this.store.reset();

        // --- Logique pour définir les IDs ---
        if (this.stage) {
            let stageId = this.binding.value;
            // Accepte aussi un objet même si le modificateur .id est présent
            if (this.binding.value && typeof this.binding.value === 'object') { // Vérifier si value est un objet
                stageId = this.binding.value.id;
                if (this.binding.value.voiture_id) { // Vérifier si voiture_id existe
                    this.store.setVoitureId(this.binding.value.voiture_id);
                }
            }
            this.store.setStageId(stageId);
        }
        if (this.voiture) {
            this.store.setVoitureId(this.voitureId);
        }
        if (this.circuit) {
            this.store.setCircuitId(this.circuitId);
        }
        // --- Fin logique IDs ---

        // --- Options additionnelles via v-open-configurateur (binding.value en objet)
        const options = this.getOptions();
        logger.log('🎯 Directive - options applied to store (process start):', JSON.stringify(options));
        if (options && typeof options === 'object') {
            // Support both direct voiture_id and nested voiture.id structure
            if (options.voiture_id) {
                this.store.setVoitureId(options.voiture_id);
            } else if (options.voiture && options.voiture.id) {
                logger.log('🎯 Directive - Setting voiture_id from nested structure:', options.voiture.id);
                this.store.setVoitureId(options.voiture.id);
            }
            if (options.circuit_id) {
                this.store.setCircuitId(options.circuit_id);
            }
            if (options.stage_id) {
                this.store.setStageId(options.stage_id);
            }
            if (options.nb_tours) {
                this.store.setNbTours(options.nb_tours);
            }
            if (options.dateMode) {
                this.store.setDateMode(options.dateMode);
            }
            if (options.possibilite) {
                this.store.setPossibilite(options.possibilite);
            }

            // Pré-application de la date si fournie par le datepicker
            if (options.selectedDateIso) {
                this.store.setDateMode(options.dateMode || 'choice');
                this.store.setPreselectedDateIso(options.selectedDateIso);
                logger.log('🎯 Directive - store.preselectedDateIso set (process):', this.store.preselectedDateIso);
            }
            if (options.circuit_date_id) {
                this.store.setCircuitDateId(parseInt(options.circuit_date_id));
            }
        }

        // --- Déterminer l'étape initiale ---
        /* désactivé on force à être sur la premiere step à chaque fois
        if (this.store.voiture_id && this.store.circuit_id) {
            this.store.setStep('tour'); // Ou 'creneau' si c'est l'étape après circuit/voiture
        } else if (this.store.voiture_id) {
            this.store.setStep('voiture'); // Si voiture est définie, l'étape suivante est circuit
        } else if (this.store.circuit_id) {
            this.store.setStep('circuit'); // Si circuit est défini, l'étape suivante est voiture
        } else if (this.store.stage_id) {
            this.store.setStep('voiture'); // Si seulement stage_id, commencer par voiture
        } else {
            // Cas par défaut si rien n'est pré-rempli (ou erreur)
            // Peut-être définir une étape par défaut ou logger une erreur
            console.warn("Impossible de déterminer l'étape initiale, défaut sur 'voiture'");
            this.store.setStep('voiture');
        }
        // --- Fin détermination étape ---

        // Surcharge éventuelle de l'étape initiale via options.step
        if (options && typeof options === 'object' && options.step) {
            const allowed = ['voiture', 'circuit', 'tour', 'creneau', 'option', 'complement'];
            if (allowed.includes(options.step)) {
                this.store.setStep(options.step);
            }
        }
        */

        this.store.setStep(this.store.first_step);

        // Attendre que le modal soit prêt (inclut fetchStep)
        this.storeGlobalLoader.start()
        logger.log('🎯 Directive - just before openModal, store.preselectedDateIso:', this.store.preselectedDateIso);
        await this.store.openModal({circuitSelectedByUser: Boolean(options.circuit_id)});
        this.storeGlobalLoader.stop()

        // Si une date a été sélectionnée via le datepicker, initialiser l'étape 4 (creneau) avec cette date
        try {
            if (options && typeof options === 'object' && options.selectedDateIso) {
                let monthSelected, dateSelected;

                // Rechercher la date dans les données chargées
                if (this.store.stage && Array.isArray(this.store.stage.months)) {
                    this.store.stage.months.forEach(month => {
                        (month.dates || []).forEach(date => {
                            if (date.date === options.selectedDateIso) {
                                monthSelected = month;
                                dateSelected = date;
                            }
                        });
                    });
                } else if (Array.isArray(this.store.stages) && this.store.stages.length) {
                    // Si plusieurs stages, essayer sur le stage courant
                    const st = this.store.stage || this.store.stages[0];
                    if (st && Array.isArray(st.months)) {
                        st.months.forEach(month => {
                            (month.dates || []).forEach(date => {
                                if (date.date === options.selectedDateIso) {
                                    monthSelected = month;
                                    dateSelected = date;
                                }
                            });
                        });
                    }
                }

                if (monthSelected && dateSelected) {
                    // Positionner la sélection pour l'étape "Date & Horaire"
                    this.store.setCurentMonth(monthSelected);
                    this.store.setCurentDate(dateSelected);
                    this.store.setStepDate('creneau');

                    // Aller directement à l'étape 4 du configurateur
                    this.store.setStep('creneau');
                    logger.log('🎯 Directive - Initialisation étape 4 avec date sélectionnée:', options.selectedDateIso);
                } else {
                    logger.warn('⚠️ Directive - Date sélectionnée non trouvée dans les données du stage:', options.selectedDateIso);
                }
            }
        } catch (e) {
            logger.warn('⚠️ Directive - Erreur lors de l\'initialisation de la date à l\'étape 4:', e);
        }
    }
}
